import {
  LBTButton,
  LBTChip,
  LBTLabel,
  LBTSpacer,
  Section,
} from '@laborability/components';
import { Box } from '@mui/material';
import {
  getCurrentSerenisCouponCode,
  requestSerenisCouponCode,
  setSnackbarError,
} from '@laborability/commons';
import styled from 'styled-components';
import { useRecoilCallback, CallbackInterface } from 'recoil';
import { useEffect, useState } from 'react';
import BoldSpan from '../../components/answerBoxes/BoldSpan';
import SerenisLogo from '../../assets/images/serenis.svg';

const StyledImageContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const StyledImage = styled.img<{ maxwidth: string }>`
  width: 100%;
  max-width: ${props => props.maxwidth};
`;
export const Image = ({ src }: { src: string }) => {
  return (
    <StyledImageContainer>
      <StyledImage src={src} maxwidth={'100%'} />
    </StyledImageContainer>
  );
};

export default function Serenis() {
  const [couponCode, setCouponCode] = useState<string | null>(null);
  const [couponValidTo, setCouponValidTo] = useState<string | null>(null);
  const [hasAssignedCoupon, setHasAssignedCoupon] = useState(false);
  const [noCouponsAvailable, setNoCouponsAvailable] = useState(false);

  const formatCouponExpirationDate = (validTo: string | null) => {
    if (!validTo) return null;

    const date = new Date(validTo);
    if (Number.isNaN(date.getTime())) return null;

    return date.toLocaleDateString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const isCouponExpired = (validTo: string | null) => {
    if (!validTo) return false;

    const date = new Date(validTo);
    if (Number.isNaN(date.getTime())) return false;

    return date.getTime() < Date.now();
  };

  const loadCurrentCoupon = useRecoilCallback(
    ({ set }: CallbackInterface) => async () => {
      try {
        const res = await getCurrentSerenisCouponCode();
        return {
          code: res?.data?.code as string | undefined,
          validTo: (res?.data?.valid_to as string | null | undefined) ?? null,
        };
      } catch (error: any) {
        if (error?.response?.status !== 404) setSnackbarError(set, error);
        return undefined;
      }
    },
    [],
  );

  const requestCoupon = useRecoilCallback(
    ({ set }: CallbackInterface) => async () => {
      try {
        const res = await requestSerenisCouponCode();
        return {
          code: res?.data?.code as string | undefined,
          validTo: (res?.data?.valid_to as string | null | undefined) ?? null,
        };
      } catch (error: any) {
        if (error?.response?.status === 404) {
          setNoCouponsAvailable(true);
        } else {
          setSnackbarError(set, error);
        }
        return undefined;
      }
    },
    [],
  );

  useEffect(() => {
    const bootstrapCoupon = async () => {
      const coupon = await loadCurrentCoupon();
      if (coupon?.code) {
        setCouponCode(coupon.code);
        setCouponValidTo(coupon.validTo);
        setHasAssignedCoupon(true);
      }
    };

    void bootstrapCoupon();
  }, [loadCurrentCoupon]);

  return (
    <Section>
      <LBTSpacer spacing={4} />
      <Box width="300px">
        <Image src={SerenisLogo} />
      </Box>
      <LBTSpacer spacing={4} />
      <Box>
        <LBTLabel variant="spGroteskSubtitle" maxWidth="980px">
          Ogni persona ha esigenze diverse. Per questo trovi su Serenis professionisti con approcci ed esperienze differenti, per costruire un percorso che si adatta a te.
        </LBTLabel>
        <LBTSpacer spacing={1} />
        <LBTLabel variant="bodyText" maxWidth="980px">
          Per iniziare ti serve solo: un ambiente tranquillo, dove riesci a concentrarti; uno spazio privato, che protegga la tua privacy; una connessione stabile, per non avere interruzioni durante la seduta.
        </LBTLabel>
        <LBTSpacer spacing={1} />
        <LBTLabel variant="bodyText" maxWidth="980px">
          La psicoterapia può aiutarti su tanti fronti della vita quotidiana:
          <div style={{ textAlign: 'center' }}>
            <ul style={{ display: 'inline-block', textAlign: 'left', paddingLeft: '20px' }}>
              <li>Comunicazione: strumenti per esprimerti in modo più chiaro, nelle relazioni di lavoro e non solo.</li>
              <li>Sonno: capire cosa c'è dietro ai disturbi del sonno e trovare strategie per riposare meglio.</li>
              <li>Genitorialità: un supporto in più per affrontare le sfide di essere genitori, con più serenità.</li>
              <li>Mindfulness: imparare a restare nel presente, per gestire lo stress e vivere le giornate con più equilibrio.</li>
            </ul>
          </div>
          Quando abbiamo la possibilità di parlarne con qualcuno, anche solo iniziare può cambiare la prospettiva.<br />Se vuoi provarci, il primo passo è qui.
        </LBTLabel>
        <LBTSpacer spacing={6} />
        <LBTLabel variant="spGroteskSubtitle" maxWidth="980px">
          Richiedi il codice e avrai accesso a 5 sedute di terapia individuale online a <strong>44€</strong> ciascuna.
        </LBTLabel>
        <LBTLabel variant="bodyText" maxWidth="980px">
          Il primo colloquio conoscitivo resta sempre gratuito, quindi si aggiunge alle sedute incluse nel codice.
        </LBTLabel>
      </Box>

      {!hasAssignedCoupon && !noCouponsAvailable && (
        <>
          <LBTSpacer spacing={6} isFixed />
          <LBTButton
            datatestid="external_button_serenis"
            variant="contained"
            color="primary-negative"
            size="medium"
            onClick={async () => {
              const coupon = await requestCoupon();
              if (coupon?.code) {
                setCouponCode(coupon.code);
                setCouponValidTo(coupon.validTo);
                setHasAssignedCoupon(true);
              }
            }}
          >
            Richiedi il codice sconto
          </LBTButton>
        </>
      )}

      {noCouponsAvailable && (
        <>
          <LBTSpacer spacing={4} />
          <LBTChip
            variant="filled"
            color="lemonCake"
            label={
                <><BoldSpan>Ops!</BoldSpan><br />nessun codice disponibile in questo momento</>
            }
            sx={{
              height: 'auto',
              '&.MuiChip-root': {
                padding: '18px 22px'
              },
              '& .MuiTypography-root': {
                fontSize: '16px !important',
                lineHeight: '22px !important',
              },
            }}
          />
        </>
      )}

      {couponCode && (
        <>
          <LBTSpacer spacing={4} />
          <LBTLabel variant="bodyText">
            Ecco il tuo codice sconto per<br />il percorso <BoldSpan>Terapia individuale</BoldSpan>
          </LBTLabel>
          <LBTSpacer spacing={1} isFixed />
          <LBTChip
            variant="filled"
            label={
                <BoldSpan>{couponCode}</BoldSpan>
            }
            sx={{
              height: 'auto',
              '&.MuiChip-root': {
                padding: '18px 22px',
                backgroundColor: isCouponExpired(couponValidTo) ? '#9B9B9B !important' : '#7A59F5 !important',
              },
              '& .MuiTypography-root': {
                color: 'white !important',
                fontSize: '22px !important',
                lineHeight: '24px !important',
              },
            }}
          />
          {formatCouponExpirationDate(couponValidTo) && (
            <>
              <LBTSpacer spacing={1} isFixed />
              <LBTLabel variant="bodyText">
                {isCouponExpired(couponValidTo) ? 'Il codice è scaduto il ' : 'Il codice è valido fino al '}
                <BoldSpan>{formatCouponExpirationDate(couponValidTo)}</BoldSpan>
              </LBTLabel>
              <LBTSpacer spacing={4} />
            </>
          )}
          {!isCouponExpired(couponValidTo) && (
            <>
              <Box>
                <LBTLabel variant="bodyText" maxWidth="980px">
                  Puoi inserire il tuo codice convenzione direttamente dalla tua area personale di Serenis:
                  <div style={{ textAlign: 'center' }}>
                    <ul style={{ display: 'inline-block', textAlign: 'left', paddingLeft: '20px' }}>
                      <li>Apri l'app o la pagina web del tuo profilo personale di Serenis. Vai nel menu, convenzione, e clicca <BoldSpan>"Aggiungi una convenzione"</BoldSpan>.</li>
                      <li>Inserisci, o incolla, il <BoldSpan>codice ricevuto</BoldSpan> e seleziona <BoldSpan>"Attiva"</BoldSpan>.</li>
                      <li>Una volta attivata, la convenzione verrà applicata automaticamente al percorso previsto: non dovrai fare altro.</li>
                      <li>Infine, aggiorna la pagina e verifica che il costo della seduta sia stato aggiornato secondo le condizioni della convenzione.</li>
                    </ul>
                  </div>
                </LBTLabel>
              </Box>
              <LBTSpacer spacing={4} />
            </>
          )}
          <LBTButton
            datatestid="external_button_serenis_start"
            variant="contained"
            color="primary-negative"
            size="medium"
            onClick={() =>
              window.open(
                // `https://app.serenis.it/start?cnv=${couponCode}`,
                `https://app.serenis.it/`,
                '_blank',
                'noopener,noreferrer',
              )
            }
          >
            Vai su Serenis
          </LBTButton>
        </>
      )}

      <LBTSpacer spacing={8} isFixed />

    </Section>
  );
}
